/**
 * 実験データの LocalStorage キャッシュ。
 *
 * - キャッシュはページ単位（origin + pathname + search）で保持する。
 *   Lambda はページ URL と targetParams（クエリ）で実験を判定するため、
 *   URL が完全一致しないページにキャッシュを流用しない。
 * - キャッシュは「表示を即時に切り替えるため」だけに使い、正しさは毎回の API 再検証で担保する
 *   （stale-while-revalidate）。実験の停止・HTML 編集・比率変更は次のページビューで反映される。
 * - DOM に依存しない純粋関数として実装し、Storage を引数で受け取る（テスト容易性のため）。
 */

export interface ExperimentCacheEntry {
  // キャッシュを保存した訪問者。visitorId が変わった（Cookie が消えた）場合は、サーバーの割り当てと食い違うため使わない
  visitorId: string;
  experimentId: string;
  caseId: string;
  pattern: string;
  xPath: string;
  customHtml: string;
  expiresAt: number;
}

type CacheStore = Record<string, ExperimentCacheEntry>;

export type CacheStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

// v1（サービス単位で1件のみ保持する形式）とは互換性がないためキーを分ける
export const CACHE_KEY_PREFIX = 'itera_experiment_v2_';
export const LEGACY_CACHE_KEY_PREFIX = 'itera_experiment_';
// 毎回 API で再検証するため、TTL は保存領域の掃除と長期離脱時の陳腐化防止のためのもの
export const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// 1サービスあたりに保持するページ数の上限
export const MAX_CACHE_ENTRIES = 20;

/**
 * キャッシュのページキーを返す。ハッシュ（#以降）は実験判定に使われないため除外する。
 */
export const getPageKey = (href: string): string => {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}${url.search}`;
  } catch {
    return href.split('#')[0] ?? href;
  }
};

const isValidEntry = (value: unknown): value is ExperimentCacheEntry => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.visitorId === 'string' &&
    v.visitorId !== '' &&
    typeof v.experimentId === 'string' &&
    v.experimentId !== '' &&
    typeof v.caseId === 'string' &&
    v.caseId !== '' &&
    typeof v.pattern === 'string' &&
    v.pattern !== '' &&
    typeof v.xPath === 'string' &&
    v.xPath !== '' &&
    typeof v.customHtml === 'string' &&
    v.customHtml !== '' &&
    typeof v.expiresAt === 'number'
  );
};

/**
 * ストアを読み込む。構造不正なエントリ・期限切れエントリは除外する。
 */
const readStore = (
  storage: CacheStorage,
  serviceKey: string,
  now: number,
): CacheStore => {
  const raw = storage.getItem(CACHE_KEY_PREFIX + serviceKey);
  if (!raw) return {};
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {};
  }
  const store: CacheStore = {};
  for (const [pageKey, entry] of Object.entries(parsed)) {
    if (isValidEntry(entry) && entry.expiresAt > now) {
      store[pageKey] = entry;
    }
  }
  return store;
};

const writeStore = (
  storage: CacheStorage,
  serviceKey: string,
  store: CacheStore,
): void => {
  if (Object.keys(store).length === 0) {
    storage.removeItem(CACHE_KEY_PREFIX + serviceKey);
    return;
  }
  storage.setItem(CACHE_KEY_PREFIX + serviceKey, JSON.stringify(store));
};

/**
 * 指定ページのキャッシュを返す。存在しない・期限切れ・読み込み失敗時は null。
 */
export const loadCacheEntry = (
  storage: CacheStorage,
  serviceKey: string,
  pageKey: string,
  now: number = Date.now(),
): ExperimentCacheEntry | null => {
  try {
    // v1 形式のキャッシュはページを区別できず誤適用の原因になるため破棄する
    storage.removeItem(LEGACY_CACHE_KEY_PREFIX + serviceKey);
    return readStore(storage, serviceKey, now)[pageKey] ?? null;
  } catch {
    return null;
  }
};

/**
 * 指定ページのキャッシュを保存する。上限を超えた場合は期限の近いものから削除する。
 */
export const saveCacheEntry = (
  storage: CacheStorage,
  serviceKey: string,
  pageKey: string,
  data: Omit<ExperimentCacheEntry, 'expiresAt'>,
  now: number = Date.now(),
): boolean => {
  try {
    let store: CacheStore = {};
    try {
      store = readStore(storage, serviceKey, now);
    } catch {
      // 壊れたストアは上書きする
    }
    store[pageKey] = { ...data, expiresAt: now + CACHE_TTL_MS };
    const pageKeys = Object.keys(store);
    if (pageKeys.length > MAX_CACHE_ENTRIES) {
      pageKeys
        .sort((a, b) => (store[a]?.expiresAt ?? 0) - (store[b]?.expiresAt ?? 0))
        .slice(0, pageKeys.length - MAX_CACHE_ENTRIES)
        .forEach((key) => {
          delete store[key];
        });
    }
    writeStore(storage, serviceKey, store);
    return true;
  } catch {
    return false;
  }
};

/**
 * 指定ページのキャッシュを削除する。
 */
export const removeCacheEntry = (
  storage: CacheStorage,
  serviceKey: string,
  pageKey: string,
  now: number = Date.now(),
): void => {
  try {
    const store = readStore(storage, serviceKey, now);
    if (!(pageKey in store)) return;
    delete store[pageKey];
    writeStore(storage, serviceKey, store);
  } catch {
    try {
      storage.removeItem(CACHE_KEY_PREFIX + serviceKey);
    } catch {
      // Storage が利用できない環境では何もしない
    }
  }
};
